<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch, nextTick, inject } from "vue"
import { useToast } from "primevue/usetoast"
import { useI18n } from "vue-i18n"
import { Services } from "@services/register"
import { useFetchWrapper } from "@core/composables"
import { ServiceError } from "@core/exceptions"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            common: {
                success: "Succès",
                error: "Erreur",
                cancel: "Annuler",
                validate: "Valider",
            },
            access: {
                title: "Gérer l'accès aux bâtiments",
                admin_message:
                    "Les administrateurs ont accès à tous les bâtiments par défaut.",
                placeholders: {
                    search: "Rechercher un bâtiment par nom ou adresse...",
                    sort: "Trier les bâtiments",
                },
                stats: {
                    buildings:
                        "{count} bâtiment • {count_access} avec accès | {count} bâtiments • {count_access} avec accès",
                    modifications:
                        "{count} modification en attente | {count} modifications en attente",
                },
                selectAll: {
                    selectAll: "Tout sélectionner",
                    deselectAll: "Tout désélectionner",
                },
                empty: {
                    noResults: "Aucun bâtiment trouvé",
                    noBuildings: "Aucun bâtiment disponible",
                },
                sort: {
                    label: "Trier par :",
                    name_asc: "Nom (A-Z)",
                    name_desc: "Nom (Z-A)",
                    address_asc: "Adresse (A-Z)",
                    address_desc: "Adresse (Z-A)",
                },
                errors: {
                    updatingAccess:
                        "Erreur lors de la mise à jour des accès. Tous les changements ont été annulés.",
                },
                messages: {
                    accessUpdatedSuccess:
                        "Les accès ont été mis à jour avec succès",
                },
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

type BuildingFormatted = {
    _id: string
    name: string
    address?: string
    icon?: string
    visible_to?: Array<{ _id_user: string }>
    access?: boolean
}

type AccessChange = {
    _id: string
    access: boolean
}

type Props = {
    /** Identifiant de l'utilisateur */
    userId: string
    /** Prénom de l'utilisateur */
    firstname: string
    /** Nom de l'utilisateur */
    lastname: string
    /** Nom d'utilisateur (email) */
    username: string
    /** Avatar de l'utilisateur */
    avatar?: string | null
    /** Liste des bâtiments avec leurs accès */
    buildings: BuildingFormatted[]
    /** État de chargement */
    loading?: boolean
    /** Indique si l'utilisateur est administrateur */
    admin?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    avatar: null,
    buildings: () => [],
    loading: false,
    admin: false,
})

const emit = defineEmits<{
    "update:access": [{ access: boolean; buildingId: string }]
}>()

/**
 * Contrôle la visibilité du dialog
 */
const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

// ================================
// COMPOSABLES
// ================================

/**
 * Injection des services pour la gestion des accès aux bâtiments
 */
const { $addAccessToBuildings, $removeAccessFromBuildings } =
    inject<Services>("services")!

/**
 * Service de gestion des toasts
 */
const toast = useToast()

// ================================
// STATE
// ================================

/**
 * Liste complète des bâtiments avec leurs accès
 */
const buildingsList = ref<BuildingFormatted[]>([])

/**
 * Copie de sauvegarde des bâtiments pour le rollback
 */
const originalBuildings = ref<BuildingFormatted[]>([])

/**
 * Liste des modifications d'accès en attente
 */
const accessChanges = ref<AccessChange[]>([])

/**
 * Terme de recherche pour filtrer les bâtiments
 */
const searchTerm = ref<string>("")

/**
 * Configuration du tri (champ et direction)
 */
const sortConfig = ref<{
    field: "name" | "address"
    direction: "asc" | "desc"
}>({
    field: "name",
    direction: "asc",
})

// ================================
// COMPUTED
// ================================

/**
 * Options de tri disponibles pour l'utilisateur
 */
const getSortOptions = computed(() => [
    {
        label: `${t("access.sort.name_asc")} ↑`,
        value: { field: "name", direction: "asc" },
    },
    {
        label: `${t("access.sort.name_desc")} ↓`,
        value: { field: "name", direction: "desc" },
    },
    {
        label: `${t("access.sort.address_asc")} ↑`,
        value: { field: "address", direction: "asc" },
    },
    {
        label: `${t("access.sort.address_desc")} ↓`,
        value: { field: "address", direction: "desc" },
    },
])

/**
 * Initialise les bâtiments depuis les props
 */
function initializeBuildings() {
    buildingsList.value = props.buildings
        .map((building) => {
            const hasAccess =
                !!building.visible_to?.find(
                    (v) => v._id_user === props.userId
                ) || props.admin

            return {
                ...building,
                access: hasAccess,
            }
        })
        .sort((a, b) => {
            // Trie : ceux avec accès en premier
            if (a.access === b.access) return 0
            return a.access ? -1 : 1
        })

    originalBuildings.value = JSON.parse(JSON.stringify(buildingsList.value))
    accessChanges.value = []
}

/**
 * Liste des bâtiments filtrés par la recherche
 * Vérifie depuis le début du nom ou de l'adresse (comme dans CompanyMembers)
 */
const getFilteredBuildings = computed(() => {
    const search = searchTerm.value.toLowerCase().trim()

    if (!search) {
        return buildingsList.value
    }

    return buildingsList.value.filter((building) => {
        const name = (building.name || "").toLowerCase()
        const address = (building.address || "").toLowerCase()

        return name.startsWith(search) || address.startsWith(search)
    })
})

/**
 * Liste des bâtiments filtrés et triés
 */
const getSortedBuildings = computed(() => {
    const sorted = [...getFilteredBuildings.value]

    sorted.sort((a, b) => {
        const aValue = (a[sortConfig.value.field] || "").toLowerCase()
        const bValue = (b[sortConfig.value.field] || "").toLowerCase()

        const comparison = aValue.localeCompare(bValue)
        return sortConfig.value.direction === "asc" ? comparison : -comparison
    })

    return sorted
})

/**
 * Nombre de bâtiments avec accès activé
 */
const getBuildingsWithAccess = computed(() => {
    return buildingsList.value.filter((building) => building.access).length
})

/**
 * Indique s'il y a des modifications en attente
 */
const getHasChanges = computed(() => {
    return accessChanges.value.length > 0
})

/**
 * Tous les bâtiments visibles sont-ils sélectionnés ?
 */
const getAllVisible = computed({
    get: () => {
        if (getFilteredBuildings.value.length === 0) return false
        return getFilteredBuildings.value.every(
            (building: BuildingFormatted) => building.access
        )
    },
    set: (value: boolean) => {
        getFilteredBuildings.value.forEach((building: BuildingFormatted) => {
            if (building.access !== value) {
                toggleAccess(building._id, value)
            }
        })
    },
})

/**
 * Initiales de l'utilisateur pour l'avatar
 */
const getInitials = computed(() => {
    const firstname = props.firstname || ""
    const lastname = props.lastname || ""
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase()
})

/**
 * Nom complet de l'utilisateur
 */
const getFullName = computed(() => {
    const firstname = capitalize(props.firstname || "")
    const lastname = capitalize(props.lastname || "")
    return `${firstname} ${lastname}`.trim()
})

// ================================
// BUSINESS LOGIC
// ================================

/**
 * Met en majuscule la première lettre d'une chaîne
 */
function capitalize(str: string): string {
    if (!str) return ""
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// ================================
// BUSINESS LOGIC (accès)
// ================================

/**
 * Active ou désactive l'accès pour un bâtiment
 * @param buildingId - Identifiant du bâtiment
 * @param access - Nouvel état de l'accès (true = accès activé, false = désactivé)
 */
function toggleAccess(buildingId: string, access: boolean): void {
    if (props.admin) return

    const buildingIndex = buildingsList.value.findIndex(
        (b) => b._id === buildingId
    )
    if (buildingIndex !== -1) {
        buildingsList.value[buildingIndex].access = access
    }

    const changeIndex = accessChanges.value.findIndex(
        (c) => c._id === buildingId
    )
    if (changeIndex !== -1) {
        accessChanges.value[changeIndex].access = access
    } else {
        const newChange = { _id: buildingId, access }
        accessChanges.value.push(newChange)
    }

    const originalBuilding = originalBuildings.value.find(
        (b) => b._id === buildingId
    )
    if (originalBuilding && originalBuilding.access === access) {
        accessChanges.value = accessChanges.value.filter(
            (c: AccessChange) => c._id !== buildingId
        )
    }
}

// ================================
// BUSINESS LOGIC (sauvegarde)
// ================================

/**
 * useFetchWrapper pour la soumission des changements d'accès
 */
const {
    fetch: submitChanges,
    isFetching: isFetchingSubmit,
    hasError: hasErrorSubmit,
    error: errorSubmit,
} = useFetchWrapper(async () => {
    if (props.admin || accessChanges.value.length === 0) return

    try {
        // Sépare les ajouts et suppressions
        const toAdd = accessChanges.value
            .filter((change: AccessChange) => change.access)
            .map((change: AccessChange) => ({
                _id_building: change._id,
                _id_user: props.userId,
            }))

        const toRemove = accessChanges.value
            .filter((change: AccessChange) => !change.access)
            .map((change: AccessChange) => ({
                _id_building: change._id,
                _id_user: props.userId,
            }))

        const promises = []
        if (toAdd.length > 0) {
            promises.push($addAccessToBuildings(toAdd))
        }
        if (toRemove.length > 0) {
            promises.push($removeAccessFromBuildings(toRemove))
        }

        await Promise.all(promises)

        // Émet les événements pour mettre à jour la liste parente
        const changesToEmit = [...accessChanges.value]
        changesToEmit.forEach((change) => {
            emit("update:access", {
                access: change.access,
                buildingId: change._id,
            })
        })

        // Met à jour l'état original
        originalBuildings.value = JSON.parse(
            JSON.stringify(buildingsList.value)
        )
        accessChanges.value = []

        toast.add({
            severity: "success",
            summary: t("common.success"),
            detail: t("access.messages.accessUpdatedSuccess"),
            life: 3000,
        })
    } catch (err: unknown) {
        await rollbackChanges()

        toast.add({
            severity: "error",
            summary: t("common.error"),
            detail: t("access.errors.updatingAccess"),
            life: 5000,
        })
        throw new ServiceError({
            message: t("access.errors.updatingAccess"),
            code: "UNKNOWN",
            technicalMessage: err instanceof Error ? err.message : String(err),
        })
    }
})

/**
 * Annule toutes les modifications en attente et restaure l'état initial
 */
async function onCancelClick(): Promise<void> {
    await rollbackChanges()
}

/**
 * Effectue un rollback complet des modifications vers l'état original
 */
async function rollbackChanges(): Promise<void> {
    buildingsList.value = JSON.parse(JSON.stringify(originalBuildings.value))
    accessChanges.value = []
    await nextTick()
}

// ================================
// INTERACTIONS
// ================================

/**
 * Gère le clic sur un bâtiment pour basculer son accès
 * @param building - Bâtiment cliqué
 */
function onBuildingClick(building: BuildingFormatted): void {
    if (isFetchingSubmit.value || props.admin) return
    toggleAccess(building._id, !building.access)
}

/**
 * Déclenche la soumission des modifications d'accès
 */
function onSubmitClick(): void {
    submitChanges()
}

// ================================
// WATCHERS
// ================================

/**
 * Initialise les bâtiments quand les props changent
 */
watch(
    () => props.buildings,
    () => {
        initializeBuildings()
    },
    { immediate: true, deep: true }
)

/**
 * Réinitialise quand le dialog se ferme
 */
watch(visible, (newVal) => {
    if (!newVal) {
        searchTerm.value = ""
        rollbackChanges()
    } else {
        initializeBuildings()
    }
})
</script>

<template>
    <div class="w-full h-full flex flex-col gap-6">
        <!-- Informations utilisateur -->
        <div class="flex items-center gap-4 pb-4 border-b border-gray-200">
            <figure
                v-if="!avatar"
                class="flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-medium text-sm uppercase w-12 h-12"
            >
                {{ getInitials }}
            </figure>
            <img
                v-else
                :src="avatar"
                :alt="getFullName"
                class="flex-shrink-0 rounded-full object-cover w-12 h-12"
            />
            <div class="flex flex-col min-w-0">
                <p class="font-medium text-gray-800 truncate">
                    {{ getFullName }}
                </p>
                <p class="text-sm text-gray-500 truncate lowercase">
                    {{ username }}
                </p>
            </div>
        </div>

        <!-- Message pour les administrateurs -->
        <BaseMessage v-if="admin" severity="info">
            {{ t("access.admin_message") }}
        </BaseMessage>

        <!-- Barre de recherche -->
        <BaseInputWrapper
            input-id="search-input"
            :label="t('access.placeholders.search')"
        >
            <template #prefix>
                <BaseInputGroupAddon>
                    <i class="pi pi-search"></i>
                </BaseInputGroupAddon>
            </template>
            <BaseInputText v-model="searchTerm" class="w-full pl-10 pr-10">
            </BaseInputText>
        </BaseInputWrapper>

        <!-- Actions et tri -->
        <div class="flex justify-between items-end flex-wrap gap-4">
            <div>
                <span class="text-sm text-gray-600">
                    {{
                        t("access.stats.buildings", {
                            count: buildingsList.length,
                            count_access: getBuildingsWithAccess,
                        })
                    }}
                </span>
            </div>
            <div class="flex flex-col gap-2 min-w-[220px]">
                <label class="block mb-1 text-sm font-medium text-gray-700">
                    {{ t("access.sort.label") }}
                </label>
                <BaseSelect
                    v-model="sortConfig"
                    :options="getSortOptions"
                    option-label="label"
                    option-value="value"
                    class="min-w-[200px]"
                />
            </div>
        </div>

        <!-- Liste des bâtiments -->
        <div class="border border-gray-200 rounded-b-lg bg-white">
            <!-- En-tête avec sélection globale -->
            <div
                class="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg"
            >
                <div class="flex items-center gap-2">
                    <BaseCheckbox
                        v-model="getAllVisible"
                        binary
                        :disabled="
                            getFilteredBuildings.length === 0 ||
                            isFetchingSubmit ||
                            admin
                        "
                    />
                    <span class="text-sm font-medium">
                        {{
                            getAllVisible
                                ? t("access.selectAll.deselectAll")
                                : t("access.selectAll.selectAll")
                        }}
                    </span>
                </div>
            </div>

            <!-- État de chargement -->
            <div v-if="loading" class="p-4">
                <BaseSkeleton
                    v-for="i in 6"
                    :key="i"
                    height="72px"
                    class="mb-2"
                />
            </div>

            <!-- État vide -->
            <div
                v-else-if="getFilteredBuildings.length === 0"
                class="flex flex-col items-center justify-center p-12 text-center"
            >
                <i class="pi pi-building text-4xl text-gray-400 mb-4"></i>
                <p class="text-gray-600 text-center">
                    {{
                        searchTerm
                            ? t("access.empty.noResults")
                            : t("access.empty.noBuildings")
                    }}
                </p>
            </div>

            <!-- Liste virtualisée des bâtiments -->
            <BaseVirtualScroller
                v-else
                :items="getSortedBuildings"
                :item-size="72"
                class="w-full"
                :style="{ height: '400px' }"
            >
                <template #item="{ item: building }">
                    <div
                        :key="building._id"
                        class="px-4 py-3 border-b border-gray-100 transition-colors min-h-[72px] flex items-center gap-3"
                        :class="{
                            'opacity-60 cursor-not-allowed':
                                admin || isFetchingSubmit,
                            'hover:bg-gray-50 cursor-pointer':
                                !admin && !isFetchingSubmit,
                        }"
                        @click="onBuildingClick(building)"
                    >
                        <ItemBuilding
                            :building="{
                                _id: building._id,
                                name: building.name,
                                niveau: building.icon,
                                address: building.address
                                    ? {
                                          street: building.address,
                                          zipcode: '',
                                          city: '',
                                      }
                                    : undefined,
                            }"
                        />
                        <BaseCheckbox
                            :model-value="building.access"
                            binary
                            :disabled="admin || isFetchingSubmit"
                            class="ml-auto"
                            @click.stop
                            @update:model-value="
                                toggleAccess(building._id, $event)
                            "
                        />
                    </div>
                </template>
            </BaseVirtualScroller>
        </div>

        <!-- Footer avec actions -->
        <div
            v-if="getHasChanges"
            class="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-200"
        >
            <div>
                <span class="text-sm text-gray-600">
                    {{
                        t("access.stats.modifications", {
                            count: accessChanges.length,
                        })
                    }}
                </span>
            </div>
            <div class="flex gap-3">
                <BaseButton
                    :label="t('common.cancel')"
                    severity="primary"
                    outlined
                    :disabled="isFetchingSubmit"
                    @click="onCancelClick"
                />
                <BaseButton
                    :label="t('common.validate')"
                    severity="primary"
                    :loading="isFetchingSubmit"
                    @click="onSubmitClick"
                />
            </div>
        </div>

        <!-- Message d'erreur lors de la soumission -->
        <BaseMessage v-if="hasErrorSubmit" severity="error">
            {{ errorSubmit?.message }}
        </BaseMessage>
    </div>
</template>
